if (!window.Eurus.loadedScript.has('kit-customizer.js')) {
  window.Eurus.loadedScript.add('kit-customizer.js');

  (() => {
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const value = (number / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const cents = parts[1] ? `${decimal}${parts[1]}` : '';
      return dollars + cents;
    };

    const formatMoney = (amount, formatString) => {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const placeholder = formatString && formatString.match(placeholderRegex);
      if (!placeholder) {
        return formatWithDelimiters(amount, 2);
      }

      let value = '';
      switch (placeholder[1]) {
        case 'amount':
          value = formatWithDelimiters(amount, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(amount, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(amount, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(amount, 0, ' ', ',');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(amount, 2, ' ', ',');
          break;
        case 'amount_with_period_and_space_separator':
          value = formatWithDelimiters(amount, 2, ' ', '.');
          break;
        default:
          value = formatWithDelimiters(amount, 2);
      }

      return formatString.replace(placeholderRegex, value);
    };

    const fontLoadCache = new Map();
    const imageLoadCache = new Map();
    const CANVAS_FONT_WEIGHT = '400';
    const STATIC_FONT_URL = 'https://cdn.shopify.com/s/files/1/1015/3305/7156/files/BJK_Trequartista-Forma.otf?v=1785506130';
    const NUMBER_LOGO_URL = 'https://cdn.shopify.com/s/files/1/1015/3305/7156/files/bjklogosingle.svg?v=1785510292';

    const hashString = (value) => {
      let hash = 0;
      for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
      }
      return Math.abs(hash).toString(36);
    };

    const loadFontFromUrl = (fontUrl) => {
      if (!fontUrl || typeof FontFace === 'undefined' || !document.fonts) {
        return Promise.resolve('Arial');
      }

      if (fontLoadCache.has(fontUrl)) {
        return fontLoadCache.get(fontUrl);
      }

      const familyName = `kit-customizer-font-${hashString(fontUrl)}`;
      const promise = new FontFace(familyName, `url("${fontUrl}")`, {
        weight: CANVAS_FONT_WEIGHT,
        style: 'normal',
      })
        .load()
        .then((loadedFont) => {
          document.fonts.add(loadedFont);
          return document.fonts.load(`${CANVAS_FONT_WEIGHT} 32px "${familyName}"`).then(() => `"${familyName}", Arial`);
        })
        .catch((error) => {
          console.error('Failed to load kit customizer font.', error);
          return 'Arial';
        });

      fontLoadCache.set(fontUrl, promise);
      return promise;
    };

    const loadImageFromUrl = (imageUrl) => {
      if (!imageUrl) {
        return Promise.resolve(null);
      }

      if (imageLoadCache.has(imageUrl)) {
        return imageLoadCache.get(imageUrl);
      }

      const promise = new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = (error) => {
          console.error('Failed to load kit customizer image.', error);
          resolve(null);
        };
        image.src = imageUrl;
      });

      imageLoadCache.set(imageUrl, promise);
      return promise;
    };

    class KitCustomizer {
      constructor(container) {
        this.container = container;
        this.sectionId = container.dataset.sectionId;
        this.basePrice = Number(container.dataset.basePrice || 0);
        this.addonPrice = Number(container.dataset.addonPrice || 0);
        this.moneyFormat = container.dataset.moneyFormat || '${{amount}}';
        this.productInfo = container.closest('.product-info');
        this.checkbox = container.querySelector('.kit-customizer__enabled');
        this.fields = container.querySelector('.kit-customizer__fields');
        this.tabButtons = Array.from(container.querySelectorAll('[data-kit-tab-target]'));
        this.tabPanels = Array.from(container.querySelectorAll('[data-kit-tab-panel]'));
        this.playerSelect = container.querySelector('.kit-customizer__select');
        this.nameInput = container.querySelector('[data-kit-input="name"]');
        this.numberInput = container.querySelector('[data-kit-input="number"]');
        this.total = container.querySelector('[data-kit-total]');
        this.fontUrl = STATIC_FONT_URL;
        this.fontFamily = 'Arial';
        this.fontLoadPromise = null;
        this.boundWindowResize = this.handleWindowResize.bind(this);
        this.boundVariantUpdate = this.handleVariantUpdate.bind(this);
      }

      init() {
        if (!this.checkbox || !this.nameInput || !this.numberInput || !this.total) {
          return;
        }

        this.tabButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.setActiveTab(button.dataset.kitTabTarget || this.getDefaultTab());
          });
        });

        this.checkbox.addEventListener('change', () => {
          this.syncState();
          if (this.checkbox.checked) {
            this.injectGalleryPreview();
            this.selectPreviewMedia();
          } else {
            this.removeGalleryPreview();
          }
          this.updateTotal();
          this.renderCanvas();
        });

        if (this.playerSelect) {
          this.playerSelect.addEventListener('change', () => {
            const option = this.playerSelect.options[this.playerSelect.selectedIndex];
            if (option) {
              if (option.dataset.playerName !== undefined) {
                this.nameInput.value = option.dataset.playerName || '';
              }
              if (option.dataset.playerNumber !== undefined) {
                this.numberInput.value = option.dataset.playerNumber || '';
              }
            }
            this.sanitizeName();
            this.sanitizeNumber();
            this.handleInputChange(this.nameInput);
            this.handleInputChange(this.numberInput);
            if (option?.value === '' && this.tabButtons.length) {
              this.setActiveTab('custom');
            }
            if (this.checkbox.checked) {
              this.injectGalleryPreview();
            }
            this.renderCanvas();
          });
        }

        this.nameInput.addEventListener('input', () => {
          this.sanitizeName();
          this.handleInputChange(this.nameInput);
          if (this.checkbox.checked) {
            this.injectGalleryPreview();
          }
          this.renderCanvas();
        });
        this.nameInput.addEventListener('blur', () => {
          this.sanitizeName();
          this.nameInput.value = this.nameInput.value.trim();
          this.renderCanvas();
        });

        this.numberInput.addEventListener('input', () => {
          this.sanitizeNumber();
          this.handleInputChange(this.numberInput);
          if (this.checkbox.checked) {
            this.injectGalleryPreview();
          }
          this.renderCanvas();
        });
        this.numberInput.addEventListener('blur', () => {
          this.sanitizeNumber();
          this.renderCanvas();
        });

        this.bindQuantityEvents();
        window.addEventListener('resize', this.boundWindowResize);
        document.addEventListener(`eurus:product-page-variant-select:updated:${this.sectionId}`, this.boundVariantUpdate);

        this.syncState();
        if (this.checkbox.checked) {
          this.injectGalleryPreview();
          this.selectPreviewMedia();
        }
        this.updateTotal();
        this.renderCanvas();
      }

      sanitizeName() {
      this.nameInput.value = this.nameInput.value
        .replace(/[^\p{L}\s.]/gu, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^\s+/g, '')
        .slice(0, 14);
    }

      sanitizeNumber() {
        this.numberInput.value = this.numberInput.value.replace(/[^\d]/g, '').slice(0, 2);
      }

      handleInputChange(input) {
        if (input.value.trim()) {
          input.classList.remove('required-picker');
        }
      }

      getQuantityInput() {
        return (
          this.productInfo?.querySelector(`#Quantity-atc-${this.sectionId}`) ||
          this.productInfo?.querySelector(`#Quantity-${this.sectionId}`) ||
          this.productInfo?.querySelector(`form#product-form-${this.sectionId} input[name="quantity"]`)
        );
      }

      bindQuantityEvents() {
        const quantityInput = this.getQuantityInput();
        if (!quantityInput || quantityInput.dataset.kitQuantityBound === 'true') {
          return;
        }

        quantityInput.dataset.kitQuantityBound = 'true';
        quantityInput.addEventListener('input', () => this.updateTotal());
        quantityInput.addEventListener('change', () => this.updateTotal());
        quantityInput.closest('[x-data]')?.querySelectorAll('button').forEach((button) => {
          button.addEventListener('click', () => {
            setTimeout(() => this.updateTotal(), 0);
          });
        });
      }

      getQuantity() {
        const quantityInput = this.getQuantityInput();
        const quantity = Number(quantityInput?.value || 1);
        return quantity > 0 ? quantity : 1;
      }

      getDefaultTab() {
        return this.playerSelect ? 'player' : 'custom';
      }

      setActiveTab(tabName = this.getDefaultTab()) {
        if (!this.tabPanels.length) {
          return;
        }

        if (!this.tabButtons.length) {
          this.tabPanels.forEach((panel) => {
            panel.hidden = false;
          });
          return;
        }

        this.tabButtons.forEach((button) => {
          const isActive = button.dataset.kitTabTarget === tabName;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          button.tabIndex = isActive ? 0 : -1;
        });

        this.tabPanels.forEach((panel) => {
          panel.hidden = panel.dataset.kitTabPanel !== tabName;
        });
      }

      syncState() {
        const enabled = this.checkbox.checked;
        this.fields.hidden = !enabled;

        if (enabled) {
          this.setActiveTab(this.getDefaultTab());
        }

        [this.nameInput, this.numberInput].forEach((input) => {
          input.disabled = !enabled;
          input.required = enabled;
          if (!enabled) {
            input.classList.remove('required-picker');
          }
        });
      }

      handleVariantUpdate(event) {
        const variantData = event.detail?.html?.querySelector('.variant-selects-main script[type="application/json"][data-selected-variant]');
        if (!variantData || variantData.textContent === 'null') {
          return;
        }

        try {
          const variant = JSON.parse(variantData.textContent);
          if (variant && typeof variant.price === 'number') {
            this.basePrice = variant.price;
            this.bindQuantityEvents();
            this.updateTotal();
            if (this.checkbox.checked) {
              requestAnimationFrame(() => {
                this.injectGalleryPreview();
                this.selectPreviewMedia();
                this.renderCanvas();
              });
            }
          }
        } catch (error) {
          console.error(error);
        }
      }

      handleWindowResize() {
        requestAnimationFrame(() => this.renderCanvas());
      }

      updateTotal() {
        const perUnitPrice = this.basePrice + (this.checkbox.checked ? this.addonPrice : 0);
        const total = perUnitPrice * this.getQuantity();
        this.total.innerHTML = formatMoney(total, this.moneyFormat);
      }

      ensureFontFamily() {
        if (!this.fontUrl) {
          return Promise.resolve(this.fontFamily);
        }

        if (!this.fontLoadPromise) {
          this.fontLoadPromise = loadFontFromUrl(this.fontUrl).then((fontFamily) => {
            this.fontFamily = fontFamily || 'Arial';
            return this.fontFamily;
          });
        }

        return this.fontLoadPromise;
      }

      getPreviewDataAttributes() {
        const previewDataKeys = [
          'nameColor',
          'numberColor',
          'nameX',
          'nameY',
          'nameFontSize',
          'nameMaxWidth',
          'numberX',
          'numberY',
          'numberFontSize',
          'textRotation'
        ];

        return previewDataKeys.reduce((attributes, key) => {
          const dataAttribute = key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
          attributes[`data-${dataAttribute}`] = this.container.dataset[key] || '';
          return attributes;
        }, {});
      }

      applyAttributes(element, attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          if (value !== '') {
            element.setAttribute(key, value);
          }
        });
      }

      findNodeByMediaId(root, mediaId) {
        if (!root) {
          return null;
        }

        return Array.from(root.querySelectorAll('[data-media-id]')).find((node) => node.dataset.mediaId === mediaId) || null;
      }

      getGallerySlides() {
        const sliderList = document.querySelector(`#media_product-${this.sectionId}`);
        return sliderList ? Array.from(sliderList.children) : [];
      }

      getLastImageSlide() {
        return [...this.getGallerySlides()].reverse().find((node) => node.dataset.type === 'image') || null;
      }

      getLastImageSlideIndex() {
        const slides = this.getGallerySlides();
        const lastImageSlide = [...slides].reverse().find((node) => node.dataset.type === 'image');
        return lastImageSlide ? slides.indexOf(lastImageSlide) : -1;
      }

      getLastStackedImageItem() {
        const stackedList = document.querySelector(`#stacked-${this.sectionId}`);
        if (!stackedList) {
          return null;
        }

        return [...stackedList.children].reverse().find((node) => node.querySelector('img')) || null;
      }

      createPreviewCanvas() {
        const canvas = document.createElement('canvas');
        canvas.className = 'kit-customization-canvas';
        canvas.dataset.kitPreviewSection = this.sectionId;
        this.applyAttributes(canvas, this.getPreviewDataAttributes());
        return canvas;
      }

      attachPreviewCanvas(host, image) {
        if (!host || !image) {
          return;
        }

        host.classList.add('relative');
        image.dataset.kitPreviewBase = this.sectionId;

        if (!host.querySelector(`.kit-customization-canvas[data-kit-preview-section="${this.sectionId}"]`)) {
          host.appendChild(this.createPreviewCanvas());
        }

        if (!image.complete) {
          image.addEventListener('load', () => this.renderCanvas(), { once: true });
        }
      }

      syncActiveThumbnail(index) {
        const thumbnailRoot = document.querySelector(`#x-product-thumbnail-${this.sectionId}`);
        const thumbnails = thumbnailRoot ? Array.from(thumbnailRoot.getElementsByClassName('x-thumbnail')) : [];
        if (!thumbnails.length || !thumbnails[index]) {
          return;
        }

        const usesOverlay = thumbnails.some((thumb) => thumb.classList.contains('quickview-border') || thumb.classList.contains('opacity-30'));

        thumbnails.forEach((thumb, thumbnailIndex) => {
          thumb.classList.remove('quickview-border', 'border');
          if (usesOverlay && thumbnailIndex !== index) {
            thumb.classList.add('opacity-30');
          } else {
            thumb.classList.remove('opacity-30');
          }
        });

        const activeThumbnail = thumbnails[index];
        activeThumbnail.classList.remove('opacity-30');
        if (usesOverlay) {
          activeThumbnail.classList.add('quickview-border');
        } else {
          activeThumbnail.classList.add('border');
        }

        activeThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      forceActiveSlide(index) {
        const slides = this.getGallerySlides();
        if (!slides.length || !slides[index]) {
          return;
        }

        slides.forEach((slide, slideIndex) => {
          slide.classList.remove('is-active', 'is-visible', 'is-prev', 'is-next');
          if (slideIndex !== index) {
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        const activeSlide = slides[index];
        activeSlide.classList.add('is-active', 'is-visible');
        activeSlide.setAttribute('aria-hidden', 'false');
        activeSlide.querySelector('.image-detail, img')?.classList.add('active');
      }

      selectPreviewMedia() {
        const activatePreview = () => {
          const lastImageIndex = this.getLastImageSlideIndex();
          if (lastImageIndex < 0) {
            return;
          }

          const splideRoot = document.querySelector(`#x-product-${this.sectionId}`);
          if (splideRoot?.splide) {
            splideRoot.splide.go(lastImageIndex);
          }

          this.forceActiveSlide(lastImageIndex);
          this.syncActiveThumbnail(lastImageIndex);
          this.getLastStackedImageItem()?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          if (splideRoot?.splide) {
            setTimeout(() => {
              if (splideRoot.splide.index !== lastImageIndex) {
                splideRoot.splide.go(lastImageIndex);
              }
              this.forceActiveSlide(lastImageIndex);
              this.syncActiveThumbnail(lastImageIndex);
            }, 120);
          }
        };

        const splideRoot = document.querySelector(`#x-product-${this.sectionId}`);
        if (splideRoot?.splide) {
          requestAnimationFrame(() => {
            requestAnimationFrame(activatePreview);
          });
          return;
        }

        document.addEventListener(`eurus:media-gallery-ready:${this.sectionId}`, () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(activatePreview);
          });
        }, { once: true });

        requestAnimationFrame(activatePreview);
      }

      removeGalleryPreview() {
        document.querySelectorAll(`.kit-customization-canvas[data-kit-preview-section="${this.sectionId}"]`).forEach((canvas) => {
          canvas.remove();
        });

        document.querySelectorAll(`img[data-kit-preview-base="${this.sectionId}"]`).forEach((image) => {
          delete image.dataset.kitPreviewBase;
        });
      }

      injectGalleryPreview() {
        const lastImageSlide = this.getLastImageSlide();
        const slideButton = lastImageSlide?.querySelector('button');
        const slideImage = lastImageSlide?.querySelector('img.image-detail') || lastImageSlide?.querySelector('img');
        this.attachPreviewCanvas(slideButton, slideImage);

        const stackedItem = this.getLastStackedImageItem();
        const stackedImage = stackedItem?.querySelector('img');
        this.attachPreviewCanvas(stackedItem, stackedImage);
      }

      renderCanvas() {
        Promise.all([this.ensureFontFamily(), loadImageFromUrl(NUMBER_LOGO_URL)]).then(([fontFamily, numberLogo]) => {
          const canvases = document.querySelectorAll(`.kit-customization-canvas[data-kit-preview-section="${this.sectionId}"]`);
          if (!canvases.length) {
            return;
          }

          canvases.forEach((canvas) => {
            const image =
              canvas.parentElement?.querySelector(`img[data-kit-preview-base="${this.sectionId}"]`) ||
              canvas.parentElement?.querySelector('img');
            if (!image || !image.naturalWidth || !image.naturalHeight) {
              return;
            }

            const rect = image.getBoundingClientRect();
            const displayWidth = Math.max(1, Math.round(rect.width || image.clientWidth || image.naturalWidth));
            const displayHeight = Math.max(1, Math.round(rect.height || image.clientHeight || image.naturalHeight));
            const pixelRatio = Math.max(1, window.devicePixelRatio || 1);

            canvas.style.width = `${displayWidth}px`;
            canvas.style.height = `${displayHeight}px`;
            canvas.width = displayWidth * pixelRatio;
            canvas.height = displayHeight * pixelRatio;

            const context = canvas.getContext('2d');
            context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
            context.imageSmoothingEnabled = false;
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'low';
            }
            context.clearRect(0, 0, displayWidth, displayHeight);

            if (!this.checkbox.checked) {
              return;
            }

            const name = this.nameInput.value.trim().toLocaleUpperCase();
            const number = this.numberInput.value.trim();
            if (!name && !number) {
              return;
            }

            const width = displayWidth;
            const height = displayHeight;
            const rotation = (Number(canvas.dataset.textRotation || 0) * Math.PI) / 180;

            if (name) {
              this.drawText(context, name, {
                x: width * Number(canvas.dataset.nameX || 0.5),
                y: height * Number(canvas.dataset.nameY || 0.34),
                fontSize: width * Number(canvas.dataset.nameFontSize || 0.065),
                fontFamily,
                maxWidth: width * Number(canvas.dataset.nameMaxWidth || 0.55),
                rotation,
                fillColor: canvas.dataset.nameColor || '#fab936',
              });
            }

            if (number) {
              const numberConfig = {
                x: width * Number(canvas.dataset.numberX || 0.5),
                y: height * Number(canvas.dataset.numberY || 0.53),
                fontSize: width * Number(canvas.dataset.numberFontSize || 0.23),
                fontFamily,
                maxWidth: width * 0.7,
                rotation,
                fillColor: canvas.dataset.numberColor || '#fab936',
              };

              if (numberLogo) {
                this.drawNumberWithLogo(context, number, numberConfig, numberLogo);
              } else {
                this.drawText(context, number, numberConfig);
              }
            }
          });
        });
      }

      fitFontSize(context, text, fontSize, fontFamily, maxWidth) {
        let fittedSize = fontSize;
        while (fittedSize > 10) {
          context.font = `${CANVAS_FONT_WEIGHT} ${fittedSize}px ${fontFamily}`;
          if (!maxWidth || context.measureText(text).width <= maxWidth) {
            return fittedSize;
          }
          fittedSize -= 2;
        }
        return fittedSize;
      }

      drawText(context, text, config) {
        if (!text) {
          return;
        }

        const fittedSize = this.fitFontSize(
          context,
          text,
          config.fontSize,
          config.fontFamily,
          config.maxWidth
        );

        context.save();
        context.translate(config.x, config.y);
        context.rotate(config.rotation);
        context.font = `${CANVAS_FONT_WEIGHT} ${fittedSize}px ${config.fontFamily}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = config.fillColor;
        context.fillText(text, 0, 0);
        context.restore();
      }

      findOpaqueBounds(context, startY = 0, endY = context.canvas.height - 1) {
        const width = context.canvas.width;
        const height = context.canvas.height;
        const fromY = Math.max(0, Math.floor(startY));
        const toY = Math.min(height - 1, Math.ceil(endY));

        if (!width || !height || toY < fromY) {
          return null;
        }

        const rowCount = toY - fromY + 1;
        const imageData = context.getImageData(0, fromY, width, rowCount).data;
        let left = width;
        let right = -1;
        let top = toY;
        let bottom = fromY;

        for (let row = 0; row < rowCount; row += 1) {
          for (let column = 0; column < width; column += 1) {
            if (imageData[(row * width + column) * 4 + 3] === 0) {
              continue;
            }

            left = Math.min(left, column);
            right = Math.max(right, column);
            top = Math.min(top, fromY + row);
            bottom = Math.max(bottom, fromY + row);
          }
        }

        if (right < 0) {
          return null;
        }

        return { left, right, top, bottom };
      }

      drawNumberWithLogo(context, text, config, logoImage) {
        if (!text || !logoImage) {
          this.drawText(context, text, config);
          return;
        }

        const fittedSize = this.fitFontSize(
          context,
          text,
          config.fontSize,
          config.fontFamily,
          config.maxWidth
        );

        context.save();
        context.translate(config.x, config.y);
        context.rotate(config.rotation);
        context.font = `${CANVAS_FONT_WEIGHT} ${fittedSize}px ${config.fontFamily}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = config.fillColor;

        const characters = Array.from(text);
        const spacing = Math.max(2, fittedSize * 0.05);
        const widths = characters.map((character) => context.measureText(character).width);
        const totalWidth = widths.reduce((sum, width) => sum + width, 0) + spacing * Math.max(0, characters.length - 1);
        let cursorX = -totalWidth / 2;

        const logoAspectRatio = logoImage.width && logoImage.height ? logoImage.width / logoImage.height : 1;
        const pixelRatio = Math.max(1, window.devicePixelRatio || 1);

        characters.forEach((character, index) => {
          const characterWidth = widths[index];
          const metrics = context.measureText(character);
          const actualBoundingBoxLeft = metrics.actualBoundingBoxLeft || characterWidth / 2;
          const actualBoundingBoxRight = metrics.actualBoundingBoxRight || characterWidth / 2;
          const characterBoxWidth = Math.max(
            characterWidth,
            actualBoundingBoxLeft + actualBoundingBoxRight
          );
          const characterBoxHeight = Math.max(
            fittedSize * 0.8,
            (metrics.actualBoundingBoxAscent || fittedSize * 0.55) + (metrics.actualBoundingBoxDescent || fittedSize * 0.25)
          );
          const characterCenterX = cursorX + characterWidth / 2;
          const paddingX = Math.max(6, Math.ceil(fittedSize * 0.12));
          const paddingY = Math.max(6, Math.ceil(fittedSize * 0.12));
          const glyphCanvasWidth = Math.ceil(characterBoxWidth + paddingX * 2);
          const glyphCanvasHeight = Math.ceil(characterBoxHeight + paddingY * 2);
          const glyphCanvas = document.createElement('canvas');
          glyphCanvas.width = Math.ceil(glyphCanvasWidth * pixelRatio);
          glyphCanvas.height = Math.ceil(glyphCanvasHeight * pixelRatio);

          const glyphContext = glyphCanvas.getContext('2d');
          if (!glyphContext) {
            context.fillText(character, characterCenterX, 0);
            cursorX += characterWidth + spacing;
            return;
          }

          glyphContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
          glyphContext.font = `${CANVAS_FONT_WEIGHT} ${fittedSize}px ${config.fontFamily}`;
          glyphContext.textAlign = 'center';
          glyphContext.textBaseline = 'middle';
          glyphContext.fillStyle = config.fillColor;
          glyphContext.imageSmoothingEnabled = false;
          if ('imageSmoothingQuality' in glyphContext) {
            glyphContext.imageSmoothingQuality = 'low';
          }

          glyphContext.fillText(character, glyphCanvasWidth / 2, glyphCanvasHeight / 2);

          const glyphBoundsRaw = this.findOpaqueBounds(glyphContext);
          if (!glyphBoundsRaw) {
            context.fillText(character, characterCenterX, 0);
            cursorX += characterWidth + spacing;
            return;
          }

          const baseLogoHeight = Math.max(6, Math.min(characterBoxHeight * 0.09, fittedSize * 0.09));
          const baseLogoWidth = baseLogoHeight * logoAspectRatio;
          const supportBandTop = Math.max(glyphBoundsRaw.top, Math.floor(glyphBoundsRaw.bottom - baseLogoHeight * pixelRatio * 1.6));
          const supportBandBottom = glyphBoundsRaw.bottom;
          const supportBoundsRaw = this.findOpaqueBounds(glyphContext, supportBandTop, supportBandBottom) || glyphBoundsRaw;
          const supportWidth = Math.max(1, (supportBoundsRaw.right - supportBoundsRaw.left + 1) / pixelRatio);
          const logoWidth = Math.min(baseLogoWidth, supportWidth * 0.7) * 0.95;
          const logoHeight = logoWidth / logoAspectRatio;
          const logoCenterX = (supportBoundsRaw.left + supportBoundsRaw.right) / (2 * pixelRatio);
          const logoCenterY = glyphBoundsRaw.bottom / pixelRatio - logoHeight * 0.62 - 3;

          glyphContext.globalCompositeOperation = 'source-atop';
          glyphContext.drawImage(
            logoImage,
            logoCenterX - logoWidth / 2,
            logoCenterY - logoHeight / 2,
            logoWidth,
            logoHeight
          );

          context.drawImage(
            glyphCanvas,
            characterCenterX - glyphCanvasWidth / 2,
            -glyphCanvasHeight / 2,
            glyphCanvasWidth,
            glyphCanvasHeight
          );
          cursorX += characterWidth + spacing;
        });

        context.restore();
      }
    }

    const initAll = (root = document) => {
      root.querySelectorAll('.kit-customizer').forEach((container) => {
        if (container.dataset.kitCustomizerReady === 'true') {
          return;
        }

        container.dataset.kitCustomizerReady = 'true';
        new KitCustomizer(container).init();
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
    } else {
      initAll();
    }

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });
  })();
}
